/**
 * Mock data for Recare detail subpage — all data keyed by range tab.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'

/* ─── KPIs ────────────────────────────────────────────────────────────────── */

export type RecareKpiData = {
  recareRate: string
  patientsDue: string
  scheduled: string
  scheduledOver: string
  overdue: string
  unscheduledValue: string
  unscheduledCount: number
  comparisonLabel: string
  recareRateDelta: string
  recareRateTrend: 'up' | 'down'
  patientsDueDelta: string
  patientsDueTrend: 'up' | 'down'
  scheduledDelta: string
  scheduledTrend: 'up' | 'down'
  overdueDelta: string
  overdueTrend: 'up' | 'down'
}

const RECARE_KPI: Record<RangeKey, RecareKpiData> = {
  '1D': {
    recareRate: '91%',
    patientsDue: '12',
    scheduled: '11',
    scheduledOver: '12',
    overdue: '1',
    unscheduledValue: '$150',
    unscheduledCount: 1,
    comparisonLabel: 'vs. yesterday',
    recareRateDelta: '3%',
    recareRateTrend: 'up',
    patientsDueDelta: '2 more',
    patientsDueTrend: 'up',
    scheduledDelta: '2 more',
    scheduledTrend: 'up',
    overdueDelta: '1 fewer',
    overdueTrend: 'down',
  },
  '1W': {
    recareRate: '88%',
    patientsDue: '54',
    scheduled: '47',
    scheduledOver: '54',
    overdue: '5',
    unscheduledValue: '$1,050',
    unscheduledCount: 7,
    comparisonLabel: 'vs. last week',
    recareRateDelta: '1%',
    recareRateTrend: 'up',
    patientsDueDelta: '4 more',
    patientsDueTrend: 'up',
    scheduledDelta: '5 more',
    scheduledTrend: 'up',
    overdueDelta: '2 fewer',
    overdueTrend: 'down',
  },
  '1M': {
    recareRate: '86%',
    patientsDue: '180',
    scheduled: '154',
    scheduledOver: '180',
    overdue: '18',
    unscheduledValue: '$3,900',
    unscheduledCount: 26,
    comparisonLabel: 'vs. last month',
    recareRateDelta: '2%',
    recareRateTrend: 'up',
    patientsDueDelta: '8 more',
    patientsDueTrend: 'up',
    scheduledDelta: '10 more',
    scheduledTrend: 'up',
    overdueDelta: '3 fewer',
    overdueTrend: 'down',
  },
  '1Y': {
    recareRate: '83%',
    patientsDue: '2,184',
    scheduled: '1,813',
    scheduledOver: '2,184',
    overdue: '124',
    unscheduledValue: '$56,550',
    unscheduledCount: 377,
    comparisonLabel: 'vs. last year',
    recareRateDelta: '4%',
    recareRateTrend: 'up',
    patientsDueDelta: '96 more',
    patientsDueTrend: 'up',
    scheduledDelta: '110 more',
    scheduledTrend: 'up',
    overdueDelta: '18 fewer',
    overdueTrend: 'down',
  },
  YTD: {
    recareRate: '84%',
    patientsDue: '510',
    scheduled: '428',
    scheduledOver: '510',
    overdue: '42',
    unscheduledValue: '$12,300',
    unscheduledCount: 82,
    comparisonLabel: 'vs. YTD last year',
    recareRateDelta: '3%',
    recareRateTrend: 'up',
    patientsDueDelta: '22 more',
    patientsDueTrend: 'up',
    scheduledDelta: '26 more',
    scheduledTrend: 'up',
    overdueDelta: '8 fewer',
    overdueTrend: 'down',
  },
}

export function getRecareKpi(range: RangeKey): RecareKpiData {
  return RECARE_KPI[range]
}

/** Parses KPI strings like `91%` to a number for charts (0–100). */
export function parseRecareRatePercent(s: string): number {
  return parseFloat(String(s).replace(/%/g, '').trim()) || 0
}

/* ─── Chart ───────────────────────────────────────────────────────────────── */

export type RecareTrendPoint = { label: string; recareRate: number }

export type RecareChartConfig = {
  title: string
  data: RecareTrendPoint[]
}

const RECARE_CHART: Record<RangeKey, RecareChartConfig> = {
  '1D': {
    title: "Today's recare completion by hour",
    data: [
      { label: '9 AM', recareRate: 100 },
      { label: '10 AM', recareRate: 67 },
      { label: '11 AM', recareRate: 100 },
      { label: '12 PM', recareRate: 0 },
      { label: '1 PM', recareRate: 50 },
      { label: '2 PM', recareRate: 100 },
      { label: '3 PM', recareRate: 0 },
      { label: '4 PM', recareRate: 0 },
    ],
  },
  '1W': {
    title: 'Recare rate by day — this week',
    data: [
      { label: 'Mon', recareRate: 85 },
      { label: 'Tue', recareRate: 90 },
      { label: 'Wed', recareRate: 88 },
      { label: 'Thu', recareRate: 86 },
      { label: 'Fri', recareRate: 91 },
    ],
  },
  '1M': {
    title: 'Recare compliance trend — last 6 months',
    data: [
      { label: 'Oct', recareRate: 79 },
      { label: 'Nov', recareRate: 82 },
      { label: 'Dec', recareRate: 80 },
      { label: 'Jan', recareRate: 84 },
      { label: 'Feb', recareRate: 84 },
      { label: 'Mar', recareRate: 86 },
    ],
  },
  '1Y': {
    title: 'Recare compliance trend — last 12 months',
    data: [
      { label: 'Apr', recareRate: 76 },
      { label: 'May', recareRate: 78 },
      { label: 'Jun', recareRate: 77 },
      { label: 'Jul', recareRate: 80 },
      { label: 'Aug', recareRate: 81 },
      { label: 'Sep', recareRate: 79 },
      { label: 'Oct', recareRate: 79 },
      { label: 'Nov', recareRate: 82 },
      { label: 'Dec', recareRate: 80 },
      { label: 'Jan', recareRate: 84 },
      { label: 'Feb', recareRate: 84 },
      { label: 'Mar', recareRate: 86 },
    ],
  },
  YTD: {
    title: 'Recare compliance YTD — Jan to Mar 2026',
    data: [
      { label: 'Jan', recareRate: 84 },
      { label: 'Feb', recareRate: 84 },
      { label: 'Mar', recareRate: 86 },
    ],
  },
}

export function getRecareChartConfig(range: RangeKey): RecareChartConfig {
  return RECARE_CHART[range]
}

/* ─── Patient recall list ─────────────────────────────────────────────────── */

export type RecarePatientRow = {
  id: string
  patient: string
  lastVisit: string
  dueDate: string
  daysOverdue: string
  hygienist: string
  interval: string
  status: 'Scheduled' | 'Overdue' | 'Not scheduled'
}

const ALL_PATIENTS: RecarePatientRow[] = [
  {
    id: '1',
    patient: 'Emily Torres',
    lastVisit: 'Sep 12, 2025',
    dueDate: 'Mar 12, 2026',
    daysOverdue: '7',
    hygienist: 'RDH Sarah Lee',
    interval: '6 months',
    status: 'Scheduled',
  },
  {
    id: '2',
    patient: 'Marcus Johnson',
    lastVisit: 'Aug 28, 2025',
    dueDate: 'Feb 28, 2026',
    daysOverdue: '19',
    hygienist: 'RDH David Wilson',
    interval: '6 months',
    status: 'Overdue',
  },
  {
    id: '3',
    patient: 'Sandra Kim',
    lastVisit: 'Jun 15, 2025',
    dueDate: 'Sep 15, 2025',
    daysOverdue: '185',
    hygienist: 'RDH Maria Garcia',
    interval: '3 months',
    status: 'Overdue',
  },
  {
    id: '4',
    patient: 'Daniel White',
    lastVisit: 'Oct 3, 2025',
    dueDate: 'Apr 3, 2026',
    daysOverdue: '—',
    hygienist: 'RDH James Patel',
    interval: '6 months',
    status: 'Not scheduled',
  },
  {
    id: '5',
    patient: 'Rachel Adams',
    lastVisit: 'Nov 20, 2025',
    dueDate: 'May 20, 2026',
    daysOverdue: '—',
    hygienist: 'RDH Sarah Lee',
    interval: '6 months',
    status: 'Scheduled',
  },
  {
    id: '6',
    patient: 'Robert Hall',
    lastVisit: 'Jul 8, 2025',
    dueDate: 'Jan 8, 2026',
    daysOverdue: '70',
    hygienist: 'RDH David Wilson',
    interval: '6 months',
    status: 'Overdue',
  },
  {
    id: '7',
    patient: 'Lisa Nguyen',
    lastVisit: 'Dec 1, 2025',
    dueDate: 'Mar 1, 2026',
    daysOverdue: '18',
    hygienist: 'RDH Maria Garcia',
    interval: '3 months',
    status: 'Scheduled',
  },
  {
    id: '8',
    patient: 'Thomas Reed',
    lastVisit: 'Oct 19, 2025',
    dueDate: 'Apr 19, 2026',
    daysOverdue: '—',
    hygienist: 'RDH James Patel',
    interval: '6 months',
    status: 'Not scheduled',
  },
]

/** For 1D show only today's appointments; 1W shows this week; broader ranges show full list. */
const PATIENT_IDS_BY_RANGE: Record<RangeKey, string[]> = {
  '1D': ['1', '7'],
  '1W': ['1', '2', '4', '7', '8'],
  '1M': ['1', '2', '3', '4', '5', '6', '7', '8'],
  '1Y': ['1', '2', '3', '4', '5', '6', '7', '8'],
  YTD: ['1', '2', '3', '4', '5', '6', '7', '8'],
}

export function getRecarePatientRows(range: RangeKey): RecarePatientRow[] {
  const ids = PATIENT_IDS_BY_RANGE[range]
  return ALL_PATIENTS.filter((p) => ids.includes(p.id))
}

/* ─── Overdue aging ───────────────────────────────────────────────────────── */

export type RecareOverdueAgingRow = {
  id: string
  agingBucket: string
  patients: number
  potentialValue: string
}

const OVERDUE_AGING: Record<RangeKey, RecareOverdueAgingRow[]> = {
  '1D': [
    { id: '1', agingBucket: '0–30 days', patients: 1, potentialValue: '$150' },
    { id: '2', agingBucket: '31–60 days', patients: 0, potentialValue: '$0' },
    { id: '3', agingBucket: '61–90 days', patients: 0, potentialValue: '$0' },
    { id: '4', agingBucket: '90+ days', patients: 0, potentialValue: '$0' },
  ],
  '1W': [
    { id: '1', agingBucket: '0–30 days', patients: 3, potentialValue: '$450' },
    { id: '2', agingBucket: '31–60 days', patients: 2, potentialValue: '$300' },
    { id: '3', agingBucket: '61–90 days', patients: 0, potentialValue: '$0' },
    { id: '4', agingBucket: '90+ days', patients: 0, potentialValue: '$0' },
  ],
  '1M': [
    { id: '1', agingBucket: '0–30 days', patients: 6, potentialValue: '$900' },
    { id: '2', agingBucket: '31–60 days', patients: 5, potentialValue: '$750' },
    { id: '3', agingBucket: '61–90 days', patients: 4, potentialValue: '$600' },
    { id: '4', agingBucket: '90+ days', patients: 3, potentialValue: '$450' },
  ],
  '1Y': [
    {
      id: '1',
      agingBucket: '0–30 days',
      patients: 42,
      potentialValue: '$6,300',
    },
    {
      id: '2',
      agingBucket: '31–60 days',
      patients: 38,
      potentialValue: '$5,700',
    },
    {
      id: '3',
      agingBucket: '61–90 days',
      patients: 26,
      potentialValue: '$3,900',
    },
    {
      id: '4',
      agingBucket: '90+ days',
      patients: 18,
      potentialValue: '$2,700',
    },
  ],
  YTD: [
    {
      id: '1',
      agingBucket: '0–30 days',
      patients: 18,
      potentialValue: '$2,700',
    },
    {
      id: '2',
      agingBucket: '31–60 days',
      patients: 14,
      potentialValue: '$2,100',
    },
    {
      id: '3',
      agingBucket: '61–90 days',
      patients: 6,
      potentialValue: '$900',
    },
    { id: '4', agingBucket: '90+ days', patients: 4, potentialValue: '$600' },
  ],
}

export function getRecareOverdueAgingRows(
  range: RangeKey,
): RecareOverdueAgingRow[] {
  return OVERDUE_AGING[range]
}

/* ─── Recare by hygienist ─────────────────────────────────────────────────── */

export type RecareByHygienistRow = {
  id: string
  hygienist: string
  due: number
  scheduled: number
  completed: number
  recareRate: string
}

const HYGIENIST_DATA: Record<RangeKey, RecareByHygienistRow[]> = {
  '1D': [
    {
      id: '1',
      hygienist: 'RDH Sarah Lee',
      due: 3,
      scheduled: 3,
      completed: 2,
      recareRate: '100%',
    },
    {
      id: '2',
      hygienist: 'RDH David Wilson',
      due: 4,
      scheduled: 3,
      completed: 3,
      recareRate: '75%',
    },
    {
      id: '3',
      hygienist: 'RDH Maria Garcia',
      due: 3,
      scheduled: 3,
      completed: 3,
      recareRate: '100%',
    },
    {
      id: '4',
      hygienist: 'RDH James Patel',
      due: 2,
      scheduled: 2,
      completed: 2,
      recareRate: '100%',
    },
  ],
  '1W': [
    {
      id: '1',
      hygienist: 'RDH Sarah Lee',
      due: 14,
      scheduled: 13,
      completed: 11,
      recareRate: '93%',
    },
    {
      id: '2',
      hygienist: 'RDH David Wilson',
      due: 16,
      scheduled: 14,
      completed: 13,
      recareRate: '88%',
    },
    {
      id: '3',
      hygienist: 'RDH Maria Garcia',
      due: 12,
      scheduled: 11,
      completed: 10,
      recareRate: '92%',
    },
    {
      id: '4',
      hygienist: 'RDH James Patel',
      due: 12,
      scheduled: 9,
      completed: 9,
      recareRate: '75%',
    },
  ],
  '1M': [
    {
      id: '1',
      hygienist: 'RDH Sarah Lee',
      due: 48,
      scheduled: 42,
      completed: 38,
      recareRate: '88%',
    },
    {
      id: '2',
      hygienist: 'RDH David Wilson',
      due: 52,
      scheduled: 44,
      completed: 40,
      recareRate: '85%',
    },
    {
      id: '3',
      hygienist: 'RDH Maria Garcia',
      due: 36,
      scheduled: 30,
      completed: 26,
      recareRate: '83%',
    },
    {
      id: '4',
      hygienist: 'RDH James Patel',
      due: 44,
      scheduled: 38,
      completed: 32,
      recareRate: '86%',
    },
  ],
  '1Y': [
    {
      id: '1',
      hygienist: 'RDH Sarah Lee',
      due: 576,
      scheduled: 502,
      completed: 468,
      recareRate: '87%',
    },
    {
      id: '2',
      hygienist: 'RDH David Wilson',
      due: 624,
      scheduled: 528,
      completed: 492,
      recareRate: '85%',
    },
    {
      id: '3',
      hygienist: 'RDH Maria Garcia',
      due: 432,
      scheduled: 360,
      completed: 324,
      recareRate: '83%',
    },
    {
      id: '4',
      hygienist: 'RDH James Patel',
      due: 552,
      scheduled: 423,
      completed: 384,
      recareRate: '77%',
    },
  ],
  YTD: [
    {
      id: '1',
      hygienist: 'RDH Sarah Lee',
      due: 144,
      scheduled: 124,
      completed: 112,
      recareRate: '86%',
    },
    {
      id: '2',
      hygienist: 'RDH David Wilson',
      due: 156,
      scheduled: 132,
      completed: 120,
      recareRate: '85%',
    },
    {
      id: '3',
      hygienist: 'RDH Maria Garcia',
      due: 108,
      scheduled: 90,
      completed: 80,
      recareRate: '83%',
    },
    {
      id: '4',
      hygienist: 'RDH James Patel',
      due: 102,
      scheduled: 82,
      completed: 72,
      recareRate: '80%',
    },
  ],
}

export function getRecareByHygienistRows(
  range: RangeKey,
): RecareByHygienistRow[] {
  return HYGIENIST_DATA[range]
}

/* ─── Recare by time period ───────────────────────────────────────────────── */

export type RecareByTimePeriodRow = {
  id: string
  timePeriod: string
  patientsDue: number
  scheduled: number
  recareRate: string
}

const TIME_PERIOD_DATA: Record<RangeKey, RecareByTimePeriodRow[]> = {
  '1D': [
    {
      id: '1',
      timePeriod: 'Today',
      patientsDue: 12,
      scheduled: 11,
      recareRate: '91%',
    },
    {
      id: '2',
      timePeriod: 'Yesterday',
      patientsDue: 10,
      scheduled: 9,
      recareRate: '90%',
    },
    {
      id: '3',
      timePeriod: 'Last week same day',
      patientsDue: 11,
      scheduled: 10,
      recareRate: '91%',
    },
  ],
  '1W': [
    {
      id: '1',
      timePeriod: 'This week',
      patientsDue: 54,
      scheduled: 47,
      recareRate: '88%',
    },
    {
      id: '2',
      timePeriod: 'Last week',
      patientsDue: 50,
      scheduled: 42,
      recareRate: '84%',
    },
    {
      id: '3',
      timePeriod: 'Last month same week',
      patientsDue: 48,
      scheduled: 40,
      recareRate: '83%',
    },
  ],
  '1M': [
    {
      id: '1',
      timePeriod: 'This month',
      patientsDue: 180,
      scheduled: 154,
      recareRate: '86%',
    },
    {
      id: '2',
      timePeriod: 'Last month',
      patientsDue: 172,
      scheduled: 144,
      recareRate: '84%',
    },
    {
      id: '3',
      timePeriod: 'Last quarter',
      patientsDue: 510,
      scheduled: 418,
      recareRate: '82%',
    },
  ],
  '1Y': [
    {
      id: '1',
      timePeriod: 'This year',
      patientsDue: 2184,
      scheduled: 1813,
      recareRate: '83%',
    },
    {
      id: '2',
      timePeriod: 'Last year',
      patientsDue: 2088,
      scheduled: 1661,
      recareRate: '80%',
    },
    {
      id: '3',
      timePeriod: '2 years ago',
      patientsDue: 1960,
      scheduled: 1548,
      recareRate: '79%',
    },
  ],
  YTD: [
    {
      id: '1',
      timePeriod: 'Jan 2026',
      patientsDue: 168,
      scheduled: 141,
      recareRate: '84%',
    },
    {
      id: '2',
      timePeriod: 'Feb 2026',
      patientsDue: 162,
      scheduled: 136,
      recareRate: '84%',
    },
    {
      id: '3',
      timePeriod: 'Mar 2026',
      patientsDue: 180,
      scheduled: 151,
      recareRate: '84%',
    },
  ],
}

export function getRecareByTimePeriodRows(
  range: RangeKey,
): RecareByTimePeriodRow[] {
  return TIME_PERIOD_DATA[range]
}
